import { APP_ROLES, AppRole } from './roles';
import Keycloak from 'keycloak-js';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class KeycloakService {
  private keycloak: Keycloak;

  constructor() {
    const base = window.location.origin;
    this.keycloak = new Keycloak({
      url: `https://rd.vigimare.simavi.ro/auth`,   // ${base} if you have reverse-proxy Keycloak put the /auth path
      realm: 'vigimare',
      clientId: 'home-app',
    });
  }
  /**
   * Initialize Keycloak at app startup.
   * - onLoad: 'login-required' forces login
   * - check-sso does silent login if already authenticated
   */
  init(): Promise<boolean> {
    return this.keycloak.init({
      onLoad: 'login-required', // or 'login-required'
      checkLoginIframe: false,
    });
  }

  canEditAreaPreference(): boolean {
    return this.hasAnyRole([
      APP_ROLES.ADMIN,
      APP_ROLES.OPERATOR,
      APP_ROLES.ANALYST,
    ]);
  }

  canDeleteIndication(): boolean {
    return this.hasAnyRole([APP_ROLES.ADMIN, APP_ROLES.OPERATOR]);
  }

  canDeleteAlert(): boolean {
    return this.hasRole(APP_ROLES.ADMIN);
  }

  canCommentOnIncident(): boolean {
    return this.hasAnyRole([APP_ROLES.ADMIN, APP_ROLES.OPERATOR]);
  }

  /** Whoever works an incident rather than only reading about it — the same pair as commenting. */
  canManageIncidents(): boolean {
    return this.hasAnyRole([APP_ROLES.ADMIN, APP_ROLES.OPERATOR]);
  }

  canViewIncidentAudit(): boolean {
    return this.hasAnyRole([APP_ROLES.ADMIN, APP_ROLES.OPERATOR]);
  }

  isLoggedIn(): boolean {
    return this.keycloak.authenticated;
  }

  getUsername(): string {
    // preferred_username is typical for Keycloak
    return (
      this.keycloak.tokenParsed?.['preferred_username'] ||
      this.keycloak.tokenParsed?.['name'] ||
      this.keycloak.tokenParsed?.['email'] ||
      ''
    );
  }

  /** Realm roles: tokenParsed.realm_access.roles */
  getRealmRoles(): string[] {
    const roles = this.keycloak.tokenParsed?.['realm_access']?.['roles'];
    return Array.isArray(roles) ? roles : [];
  }

  /** Client roles: tokenParsed.resource_access[clientId].roles */
  getClientRoles(clientId = 'home-app'): string[] {
    const access = this.keycloak.tokenParsed?.['resource_access'];
    const roles = access?.[clientId]?.['roles'];
    return Array.isArray(roles) ? roles : [];
  }

  /** Combined roles (deduped) */
  getAllRoles(): string[] {
    const all = [...this.getRealmRoles(), ...this.getClientRoles()];
    return Array.from(new Set(all)).sort();
  }

  getToken(): string | undefined {
    return this.keycloak.token;
  }

  async updateToken(minValiditySeconds = 30): Promise<string | undefined> {
    if (!this.keycloak.token) return undefined;
    await this.keycloak.updateToken(minValiditySeconds);
    return this.keycloak.token;
  }

  get tokenParsed(): any {
    return this.keycloak.tokenParsed ?? {};
  }

  get username(): string {
    return this.tokenParsed?.preferred_username ?? '';
  }

  get roles(): AppRole[] {
    const roles = this.tokenParsed?.realm_access?.roles ?? [];

    return roles.filter((role: string) =>
      Object.values(APP_ROLES).includes(role as AppRole)
    ) as AppRole[];
  }

  hasRole(role: AppRole): boolean {
    return this.roles.includes(role);
  }

  hasAnyRole(roles: AppRole[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  isAdmin(): boolean {
    return this.hasRole(APP_ROLES.ADMIN);
  }

  async logout(): Promise<void> {
    // redirect back to app
    await this.keycloak.logout({ redirectUri: window.location.origin });
  }
}


